import React, { useState } from 'react';
import { 
  Button, 
  Paper, 
  Typography, 
  CircularProgress, 
  Alert, 
  Box, 
  Divider, 
  List, 
  ListItem, 
  ListItemText,
  Avatar,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Download, 
  RefreshCw, 
  Star, 
  MessageCircle, 
  Calendar,
  Clock,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { supabase } from '../../lib/supabase';

const ReviewFetcher = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [recentReviews, setRecentReviews] = useState([]);
  const [stats, setStats] = useState(null);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setResult(null);
      
      // Call our server endpoint instead of the BrightLocal API directly
      const response = await axios.post('http://localhost:3069/api/fetch-reviews');
      setResult(response.data);
      
      // After fetching, get the most recent reviews
      await fetchRecentReviews();
    } catch (error) {
      setResult({
        success: false,
        message: error.message || 'An error occurred while fetching reviews'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentReviews = async () => {
    try {
      // Get the 10 most recent reviews with platform field
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .not('platform', 'is', null)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentReviews(data || []);

      // Get all reviews with platform field
      const { data: allReviews, error: allError } = await supabase
        .from('feedback')
        .select('platform')
        .not('platform', 'is', null);

      if (allError) throw allError;
      
      // Calculate stats manually
      if (allReviews && allReviews.length > 0) {
        const platformCounts = allReviews.reduce((acc, review) => {
          acc[review.platform] = (acc[review.platform] || 0) + 1;
          return acc;
        }, {});
        
        const statsData = Object.keys(platformCounts).map(platform => ({
          platform,
          count: platformCounts[platform]
        }));
        
        setStats(statsData || []);
      } else {
        setStats([]);
      }
    } catch (error) {
      console.error('Error fetching recent reviews:', error);
    }
  };

  // Fetch recent reviews on component mount
  React.useEffect(() => {
    fetchRecentReviews();
  }, []);

 // Function to get initials from name
 const getInitials = (name) => {
  if (!name) return '';
  
  const parts = name.split(' ').filter(part => part.trim() !== '');
  
  if (parts.length > 1) {
    const firstInitial = parts[0] && parts[0][0] ? parts[0][0] : '';
    const lastInitial = parts[parts.length-1] && parts[parts.length-1][0] ? parts[parts.length-1][0] : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }
  
  return parts[0] && parts[0][0] ? parts[0][0].toUpperCase() : '';
};

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatPlatformName = (platform) => {
    if (!platform) return '';
    if (platform === 'Google Maps') return 'Google';
    return platform;
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <Paper className="p-0 mb-6 overflow-hidden shadow-md border border-gray-200 rounded-lg">
      <Box className="bg-gradient-to-r from-white-600 to-white-800 p-6">
        <div className="flex justify-between items-center">
          <div>
            <Typography variant="h5" component="h2" className="text-white font-semibold">
              External Review Management
            </Typography>
            <Typography variant="body2" className="text-blue-100 mt-1">
              Fetch and manage reviews from Google, Yelp, Healthgrades, and other platforms
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            className="bg-white text-blue-700 hover:bg-blue-50"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Download />}
            onClick={fetchReviews}
            disabled={loading}
          >
            {loading ? 'Fetching...' : 'Fetch Reviews Now'}
          </Button>
        </div>
      </Box>

      {result && (
        <Alert 
          severity={result.success ? "success" : "error"} 
          className="m-4"
        >
          {result.message}
        </Alert>
      )}

      <Box className="p-6">
        <Grid container spacing={4}>
          {/* Platform Stats */}
          <Grid item xs={12} md={4}>
            <Card variant="outlined" className="h-full">
              <CardContent>
                <div className="flex justify-between items-center mb-4">
                  <Typography variant="h6" className="font-semibold text-gray-800">
                    Platform Statistics
                  </Typography>
                  <Tooltip title="Refresh statistics">
                    <IconButton 
                      size="small"
                      onClick={fetchRecentReviews}
                      className="text-blue-600"
                    >
                      <RefreshCw size={16} />
                    </IconButton>
                  </Tooltip>
                </div>
                
                {stats && stats.length > 0 ? (
                  <List className="divide-y divide-gray-100">
                    {stats.map((stat, index) => (
                      <ListItem key={index} className="py-3 px-0">
                        <ListItemText 
                          primary={
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-gray-700">{formatPlatformName(stat.platform)}</span>
                              <Chip 
                                label={stat.count} 
                                size="small" 
                                className="bg-blue-100 text-blue-800"
                              />
                            </div>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box className="text-center py-8 text-gray-500">
                    <MessageCircle className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <Typography variant="body2">
                      No platform statistics available yet
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Recent Reviews */}
          <Grid item xs={12} md={8}>
            <Card variant="outlined" className="h-full">
              <CardContent>
                <Typography variant="h6" className="font-semibold text-gray-800 mb-4">
                  Recent External Reviews
                </Typography>
                
                {recentReviews.length > 0 ? (
                  <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2" style={{ scrollbarWidth: 'thin' }}>
                    {recentReviews.map((review, index) => (
                      <Box 
                        key={index} 
                        className="p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-3">
                          <Avatar 
                            className="bg-gradient-to-br from-blue-50 to-blue-100 text-blue-700"
                          >
                            {getInitials(review.name)}
                          </Avatar>
                          
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <div>
                                <Typography variant="subtitle1" className="font-medium text-gray-800">
                                  {review.name || 'Anonymous'}
                                </Typography>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mt-0.5">
                                  <Chip 
                                    label={formatPlatformName(review.platform)} 
                                    size="small"
                                    className="bg-blue-50 text-blue-700 text-xs"
                                  />
                                  <div className="flex items-center">
                                    <Calendar size={14} className="mr-1" />
                                    {formatDate(review.created_at)}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center">
                                <Typography 
                                  variant="subtitle1" 
                                  className={`font-semibold ${getRatingColor(review.rating)}`}
                                >
                                  {review.rating}
                                </Typography>
                                <Star 
                                  size={18} 
                                  className={`ml-1 ${getRatingColor(review.rating)} fill-current`} 
                                />
                              </div>
                            </div>
                            
                            {review.review_text && (
                              <Typography 
                                variant="body2" 
                                className="mt-2 text-gray-700 bg-gray-50 p-3 rounded-md"
                              >
                                "{review.review_text}"
                              </Typography>
                            )}
                          </div>
                        </div>
                      </Box>
                    ))}
                  </div>
                ) : (
                  <Box className="text-center py-12 text-gray-500">
                    <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <Typography>
                      No external reviews available yet
                    </Typography>
                    <Typography variant="body2" className="mt-1">
                      Click "Fetch Reviews Now" to import reviews from external platforms
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ReviewFetcher;
